import {
    Close,
    CloudUpload,
    PhotoCamera,
    Refresh,
    RotateLeft,
    RotateRight,
    Save,
    ZoomIn,
    ZoomOut
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Slider,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useMicrospecDynamicUIContext } from '../GlobalProvider';
import { useMicrospecThemeLanguageContext } from '../Themes';

// Utility function to create image from canvas
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

// Utility function to get cropped image
const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, quality = 0.8) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.drawImage(
        image,
        safeArea / 2 - image.width * 0.5,
        safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(
        data,
        Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
        Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                resolve(blob);
            },
            'image/jpeg',
            quality
        );
    });
};

// Utility function to compress image
const compressImage = (blob, maxWidth = 400, quality = 0.85) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
            canvas.width = img.width * ratio;
            canvas.height = img.height * ratio;

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(resolve, 'image/jpeg', quality);
        };

        img.src = URL.createObjectURL(blob);
    });
};

/**
 * Generic Image Upload Dialog with Crop and Rotation
 * 
 * A reusable component for uploading images with cropping and rotation capabilities.
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog closes
 * @param {function} onImageUploaded - Callback when image is successfully uploaded (receives downloadURL)
 * @param {string} currentImageUrl - URL of current image to display (optional)
 * @param {string} storageRef - Firebase Storage path where image will be saved (required)
 * @param {string} title - Custom dialog title (optional, defaults to localized "Upload Image")
 * @param {string} description - Custom description text (optional)
 * @param {number} aspectRatio - Crop aspect ratio (default: 1 for square)
 * @param {string} cropShape - 'round' or 'rect' (default: 'round')
 * @param {number} maxWidth - Max width for compressed image in pixels (default: 400)
 * @param {number} quality - JPEG quality 0-1 (default: 0.85)
 * @param {number} maxFileSize - Max file size in bytes (default: 10MB)
 * 
 * @example
 * <UploadAvatarDialog 
 *   open={true}
 *   onClose={handleClose}
 *   onImageUploaded={(url) => console.log('New image:', url)}
 *   currentImageUrl={user.photoURL}
 *   storageRef={`users/${userId}/avatar.jpg`}
 *   title="Upload Profile Picture"
 *   aspectRatio={1}
 *   cropShape="round"
 * />
 */
const UploadAvatarDialog = ({
    open,
    onClose,
    onImageUploaded,
    currentImageUrl = null,
    storageRef,
    title = null,
    description = null,
    aspectRatio = 1,
    cropShape = 'round',
    maxWidth = 400,
    quality = 0.85,
    maxFileSize = 10 * 1024 * 1024, // 10MB default
}) => {
    const theme = useTheme();
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    // State management
    const [selectedImage, setSelectedImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [step, setStep] = useState('select'); // 'select', 'crop', 'uploading'

    const fileInputRef = useRef(null);

    // Handle file selection
    const handleFileSelect = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError(localizationProvider.translateByType({
                    type: "Internal",
                    text: "invalidFileType"
                }) || 'Please select an image file');
                return;
            }

            if (file.size > maxFileSize) {
                setError(localizationProvider.translateByType({
                    type: "Internal",
                    text: "fileTooLarge"
                }) || `File size must be less than ${maxFileSize / (1024 * 1024)}MB`);
                return;
            }

            setError(null);
            const reader = new FileReader();
            reader.onload = () => {
                setSelectedImage(reader.result);
                setStep('crop');
            };
            reader.readAsDataURL(file);
        }
    }, [localizationProvider, maxFileSize]);

    // Handle crop complete
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Handle dialog close
    const handleClose = useCallback(() => {
        if (isUploading) return; // Prevent closing during upload

        setSelectedImage(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(null);
        setError(null);
        setSuccess(false);
        setStep('select');

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        onClose();
    }, [isUploading, onClose]);

    // Upload cropped image
    const handleUpload = useCallback(async () => {
        if (!selectedImage || !croppedAreaPixels || !storageRef) return;

        setIsUploading(true);
        setError(null);

        try {
            // Get cropped image blob
            const croppedImageBlob = await getCroppedImg(
                selectedImage,
                croppedAreaPixels,
                rotation,
                0.9 // High quality for image
            );

            // Compress the cropped image
            const compressedBlob = await compressImage(croppedImageBlob, maxWidth, quality);

            // Create file from blob
            const file = new File(
                [compressedBlob],
                `image-${Date.now()}.jpg`,
                { type: 'image/jpeg' }
            );

            let downloadURL;

            // Use the existing uploadSingleFileToStorage helper
            try {
                // Import the helper function
                const { default: uploadSingleFileToStorage } = await import('../Helpers/saveFileToStorage');

                // Upload using the existing pattern
                downloadURL = await uploadSingleFileToStorage(cloudProvider.storage, storageRef, file);

                console.log('Image uploaded successfully:', downloadURL);
            } catch (storageError) {
                console.error('Storage upload error:', storageError);
                throw new Error('Failed to upload image: ' + storageError.message);
            }

            setSuccess(true);
            setStep('select');
            setSelectedImage(null);

            // Call callback if provided
            if (onImageUploaded) {
                await onImageUploaded(downloadURL);
            }

            // Auto close after success
            setTimeout(() => {
                handleClose();
            }, 1500);

        } catch (error) {
            console.error('Error uploading image:', error);
            setError(localizationProvider.translateByType({
                type: "Internal",
                text: "failedToUploadImage"
            }) || 'Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
        }
    }, [selectedImage, croppedAreaPixels, rotation, storageRef, cloudProvider, maxWidth, quality, localizationProvider, onImageUploaded, handleClose]);

    // Reset to selection step
    const handleReset = useCallback(() => {
        setSelectedImage(null);
        setStep('select');
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    py: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        <PhotoCamera />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {title || localizationProvider.translateByType({
                            type: "Internal",
                            text: "uploadImage"
                        }) || 'Upload Image'}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={isUploading}
                    sx={{ color: 'white' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 3, minHeight: 400 }}>
                {/* Success Message */}
                {success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2, borderRadius: 2 }}
                        onClose={() => setSuccess(false)}
                    >
                        {localizationProvider.translateByType({
                            type: "Internal",
                            text: "imageUploadedSuccessfully"
                        }) || 'Image uploaded successfully!'}
                    </Alert>
                )}

                {/* Error Message */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* File Selection Step */}
                {step === 'select' && (
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                {localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "selectImage"
                                }) || 'Select Image'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {description || localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "selectImageDescription"
                                }) || 'Choose an image to upload. You\'ll be able to crop and adjust it before saving.'}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                width: 200,
                                height: 200,
                                borderRadius: cropShape === 'round' ? '50%' : 2,
                                border: `3px dashed ${theme.palette.primary.main}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}10 100%)`,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    transform: 'scale(1.02)',
                                    boxShadow: `0 8px 25px ${theme.palette.primary.main}30`
                                }
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {currentImageUrl ? (
                                <Avatar
                                    src={currentImageUrl}
                                    sx={{ 
                                        width: 160, 
                                        height: 160,
                                        borderRadius: cropShape === 'round' ? '50%' : 2
                                    }}
                                    variant={cropShape === 'round' ? 'circular' : 'rounded'}
                                />
                            ) : (
                                <Stack alignItems="center" spacing={2}>
                                    <CloudUpload
                                        sx={{
                                            fontSize: 48,
                                            color: theme.palette.primary.main
                                        }}
                                    />
                                    <Typography variant="body2" color="primary">
                                        {localizationProvider.translateByType({
                                            type: "Internal",
                                            text: "clickToUpload"
                                        }) || 'Click to Upload'}
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                                }
                            }}
                        >
                            {localizationProvider.translateByType({
                                type: "Internal",
                                text: "selectImage"
                            }) || 'Select Image'}
                        </Button>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </Stack>
                )}

                {/* Crop Step */}
                {step === 'crop' && selectedImage && (
                    <Stack spacing={3}>
                        <Typography variant="h6" textAlign="center">
                            {localizationProvider.translateByType({
                                type: "Internal",
                                text: "cropYourImage"
                            }) || 'Crop Your Image'}
                        </Typography>

                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: 300,
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: `2px solid ${theme.palette.divider}`
                            }}
                        >
                            <Cropper
                                image={selectedImage}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={aspectRatio}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                                cropShape={cropShape}
                                showGrid={false}
                            />
                        </Box>

                        {/* Controls */}
                        <Stack spacing={2}>
                            {/* Zoom Control */}
                            <Box>
                                <Typography gutterBottom variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ZoomOut fontSize="small" />
                                    Zoom
                                    <ZoomIn fontSize="small" />
                                </Typography>
                                <Slider
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(e, zoom) => setZoom(zoom)}
                                    sx={{
                                        '& .MuiSlider-thumb': {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                                        }
                                    }}
                                />
                            </Box>

                            {/* Rotation Control */}
                            <Box>
                                <Typography gutterBottom variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                    <RotateLeft fontSize="small" />
                                    Rotation
                                    <RotateRight fontSize="small" />
                                </Typography>
                                <Slider
                                    value={rotation}
                                    min={-180}
                                    max={180}
                                    step={1}
                                    onChange={(e, rotation) => setRotation(rotation)}
                                    sx={{
                                        '& .MuiSlider-thumb': {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                                        }
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                )}

                {/* Uploading Step */}
                {step === 'uploading' && (
                    <Stack spacing={3} alignItems="center">
                        <CircularProgress size={80} thickness={4} />
                        <Typography variant="h6">
                            {localizationProvider.translateByType({
                                type: "Internal",
                                text: "uploadingImage"
                            }) || 'Uploading Image...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Please wait while we process and upload your image
                        </Typography>
                    </Stack>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                {step === 'crop' && (
                    <>
                        <Button
                            onClick={handleReset}
                            startIcon={<Refresh />}
                            disabled={isUploading}
                            sx={{ borderRadius: 2 }}
                        >
                            {localizationProvider.translateByType({
                                type: "Internal",
                                text: "selectDifferent"
                            }) || 'Select Different'}
                        </Button>
                        <Button
                            onClick={handleUpload}
                            variant="contained"
                            startIcon={isUploading ? <CircularProgress size={20} /> : <Save />}
                            disabled={isUploading || !croppedAreaPixels}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                                }
                            }}
                        >
                            {isUploading ? 'Uploading...' : (
                                localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "saveImage"
                                }) || 'Save Image'
                            )}
                        </Button>
                    </>
                )}

                {step === 'select' && (
                    <Button
                        onClick={handleClose}
                        disabled={isUploading}
                        sx={{ borderRadius: 2, ml: 'auto' }}
                    >
                        {localizationProvider.translateByType({
                            type: "Internal",
                            text: "cancel"
                        }) || 'Cancel'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UploadAvatarDialog;